(() => {
  const ROOT_SEL = "[data-zipdelivery-pdp]";
  const CFG_SEL = "[data-zipdelivery-config]";

  const MODAL_SEL = "[data-zipdelivery-pdp-modal]";
  const OPEN_SEL = "[data-zipdelivery-pdp-open], [data-zipdelivery-check]";
  const CLOSE_SEL = "[data-zipdelivery-pdp-close]";
  const FORM_SEL = "[data-zipdelivery-pdp-form]";
  const MODAL_INPUT_SEL = "[data-zipdelivery-pdp-input]";
  const MODAL_ERR_SEL = "[data-zipdelivery-pdp-error]";

  const INLINE_INPUT_SEL = "[data-zipdelivery-input]";
  const DATE_SEL = "[data-zipdelivery-date]";
  const ZIP_SEL = "[data-zipdelivery-zip]";
  const NOTSERVED_MSG_SEL = "[data-zipdelivery-notserved-message]";

  const normalizeZip = (zipRaw) => String(zipRaw || "").replace(/\s+/g, "").trim();

  const safeJSON = (el) => {
    if (!el) return null;
    try {
      return JSON.parse((el.textContent || "").trim());
    } catch (_) {
      return null;
    }
  };

  // Some themes override [hidden]. Force display none when hidden.
  const setHidden = (el, hidden) => {
    if (!el) return;
    el.hidden = !!hidden;
    el.style.display = hidden ? "none" : "";
  };

  // ---------- Portal modal to <body> (fixes fixed-position being trapped by transforms) ----------
const ensureModalPortalled = (root) => {
  // Cache reference on the root so we can always access it after portalling
  if (root.__zipPdpModal && document.body.contains(root.__zipPdpModal)) return root.__zipPdpModal;

  const modal = root.querySelector(MODAL_SEL);
  if (!modal) return null;

  // Move once
  if (!modal.__ported) {
    document.body.appendChild(modal);
    modal.__ported = true;
  }

  // Remember ownership so submit/close knows which root to update
  modal.__ownerRoot = root;
  root.__zipPdpModal = modal;

  return modal;
};

  const showState = (root, state) => {
    root.querySelectorAll("[data-zipdelivery-state]").forEach((el) => {
      setHidden(el, el.getAttribute("data-zipdelivery-state") !== state);
    });
  };

  // Cache (matches overlay)
  const getZipCache = (storageKey, ttlDays) => {
    const key = storageKey || "site_zip";
    const tsKey = `${key}_ts`;

    const zip = localStorage.getItem(key);
    const ts = Number(localStorage.getItem(tsKey) || 0);
    if (!zip) return null;

    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    if (ageDays > Number(ttlDays || 30)) {
      localStorage.removeItem(key);
      localStorage.removeItem(tsKey);
      return null;
    }
    return zip;
  };

  const setZipCache = async (cfg, zip) => {
    const key = cfg.storageKey || "site_zip";
    const tsKey = `${key}_ts`;

    localStorage.setItem(key, zip);
    localStorage.setItem(tsKey, String(Date.now()));

    if (cfg.setCartAttribute) {
      try {
        await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            attributes: { [cfg.cartAttributeKey || "delivery_zip"]: zip },
          }),
        });
      } catch (_) {}
    }
  };

  // -------- Date helpers (weekly + every 2nd weekday) --------
  const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  const parseDayLabel = (label) => {
    const t = String(label || "").trim();
    if (!t) return null;

    // "Every 2nd Wednesday"
    const m = t.match(/^Every\s*2nd\s*(.+)$/i);
    if (m && m[1]) return { cadence: "fortnightly", day: m[1].trim() };

    // "Monday"
    return { cadence: "weekly", day: t };
  };

  const dayIndex = (dayName) => WEEKDAYS.indexOf(String(dayName || "").trim());

  const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };

  const addDays = (d, n) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
  };

  const nextWeeklyDate = (today, targetDow) => {
    const t = startOfDay(today);
    const diff = (targetDow - t.getDay() + 7) % 7;
    return addDays(t, diff === 0 ? 0 : diff);
  };

  const parseAnchorDate = (s) => {
    // accepts "YYYY-MM-DD" or any Date-parsable string
    if (!s) return null;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : startOfDay(d);
  };

  const isFortnightOnWeek = (candidate, anchor) => {
    // candidate + anchor are startOfDay dates
    if (!anchor) return true; // if missing anchor, treat as always on
    const msPerWeek = 7 * 24 * 60 * 60 * 1000;
    const diffWeeks = Math.floor((startOfDay(candidate) - startOfDay(anchor)) / msPerWeek);
    // same parity => "on" weeks (0,2,4...) from anchor
    return Math.abs(diffWeeks) % 2 === 0;
  };

  const nextFortnightDate = (today, targetDow, anchor) => {
    // Find the next date matching weekday AND on the correct fortnight parity
    let candidate = nextWeeklyDate(today, targetDow);

    // If today is the day but off-week, jump 7 days until parity matches
    for (let i = 0; i < 6; i++) {
      if (isFortnightOnWeek(candidate, anchor)) return candidate;
      candidate = addDays(candidate, 7);
    }
    return candidate;
  };

  const ordinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const formatPrettyDate = (d) => {
    if (!d) return "";
    const weekday = new Intl.DateTimeFormat(undefined, { weekday: "long" }).format(d);
    const month = new Intl.DateTimeFormat(undefined, { month: "long" }).format(d);
    const dayNum = d.getDate();
    return `${weekday} ${ordinal(dayNum)} ${month}`;
  };

  const computeNextDeliveryDate = (daysLabels, fortnightAnchorRaw) => {
    const labels = Array.isArray(daysLabels) ? daysLabels : [];
    if (!labels.length) return null;

    const anchor = parseAnchorDate(fortnightAnchorRaw);
    const today = new Date();

    const candidates = labels
      .map(parseDayLabel)
      .filter(Boolean)
      .map(({ cadence, day }) => {
        const dow = dayIndex(day);
        if (dow < 0) return null;

        if (cadence === "fortnightly") return nextFortnightDate(today, dow, anchor);
        return nextWeeklyDate(today, dow);
      })
      .filter(Boolean);

    if (!candidates.length) return null;
    candidates.sort((a, b) => a.getTime() - b.getTime());
    return candidates[0];
  };

  // -------- Served lookup (prefers overlay helper) --------
  const getDaysForZip = (zip, triggerId) => {
    try {
      if (window.ZipDelivery && typeof window.ZipDelivery.getDaysForZip === "function") {
        const res = window.ZipDelivery.getDaysForZip(zip, { triggerId });
        return Array.isArray(res) ? res : [];
      }
    } catch (_) {}
    return [];
  };

  // -------- UI render --------
  const render = (root, cfg, zip) => {
    const inlineInput = root.querySelector(INLINE_INPUT_SEL);
    const dateEl = root.querySelector(DATE_SEL);
    const zipEls = root.querySelectorAll(ZIP_SEL);
    const notServedMsgEl = root.querySelector(NOTSERVED_MSG_SEL);

    if (inlineInput && !inlineInput.value) inlineInput.value = zip || "";

    if (!zip) {
      showState(root, "nozip");
      return;
    }

    const days = getDaysForZip(zip, cfg.triggerId);
    const served = Array.isArray(days) && days.length > 0;

    // Always set ZIP text (underlined + clickable)
    zipEls.forEach((el) => (el.textContent = zip));

    if (!served) {
      if (notServedMsgEl) {
        const msg = String(cfg.notServedMessage || "Sorry — we don’t currently deliver to {zip}.")
          .replaceAll("{zip}", zip);
        notServedMsgEl.textContent = msg.replace(zip, "").trim(); // keep zip in the underlined element
        if (!notServedMsgEl.textContent) notServedMsgEl.textContent = "Sorry — we don’t currently deliver to";
      }
      showState(root, "notserved");
      return;
    }

    const nextDate = computeNextDeliveryDate(days, cfg.fortnightAnchor);
    if (dateEl) dateEl.textContent = formatPrettyDate(nextDate);

    showState(root, "served");
  };

  // -------- Modal open/close --------
  const openModal = (root, cfg) => {
  const modal = ensureModalPortalled(root);
  if (!modal) return;

  // Store cfg on modal so submit handler can access it
  modal.__cfg = cfg;

  const input = modal.querySelector(MODAL_INPUT_SEL);
  const err = modal.querySelector(MODAL_ERR_SEL);

  if (err) {
    err.textContent = "";
    setHidden(err, true);
  }

  // prefill from cache
  const cached = getZipCache(cfg.storageKey, cfg.ttlDays);
  if (input) input.value = cached || "";

  setHidden(modal, false);
  setTimeout(() => input?.focus(), 30);
};

const closeModal = (root) => {
  const modal = root.__zipPdpModal || root.querySelector(MODAL_SEL);
  if (!modal) return;
  setHidden(modal, true);
};

  // -------- Init each block --------
  const initRoot = (root) => {
    const cfg = {
      triggerId: "zip-check",
      storageKey: "site_zip",
      ttlDays: 30,
      fortnightAnchor: null,
      notServedMessage: "Sorry — we don’t currently deliver to {zip}.",
      setCartAttribute: true,
      cartAttributeKey: "delivery_zip",
      ...(safeJSON(root.querySelector(CFG_SEL)) || {}),
    };

    // Ensure modal exists and is portalled so its events work even when moved to <body>
const modal = ensureModalPortalled(root);

// Bind modal handlers ONCE
if (modal && !modal.__handlersBound) {
  modal.__handlersBound = true;

  // Close buttons / backdrop
  modal.addEventListener("click", (e) => {
    const closeBtn = e.target.closest(CLOSE_SEL);
    if (!closeBtn) return;
    e.preventDefault();

    const owner = modal.__ownerRoot;
    if (owner) closeModal(owner);
    else setHidden(modal, true);
  });

  // Submit form (lives inside modal, so listen here)
  modal.addEventListener("submit", async (e) => {
    const form = e.target.closest(FORM_SEL);
    if (!form) return;

    e.preventDefault();

    const owner = modal.__ownerRoot;
    const cfgLive = modal.__cfg || modal.__ownerRoot?.__cfgFallback; // fallback if needed
    if (!owner || !cfgLive) return;

    const input = modal.querySelector(MODAL_INPUT_SEL);
    const err = modal.querySelector(MODAL_ERR_SEL);

    const zip = normalizeZip(input?.value);
    if (!zip || zip.length < 4) {
      if (err) {
        err.textContent = "Please enter a valid ZIP code.";
        setHidden(err, false);
      }
      input?.focus();
      return;
    }

    if (err) {
      err.textContent = "";
      setHidden(err, true);
    }

    await setZipCache(cfgLive, zip);

    // Update the block UI immediately
    render(owner, cfgLive, zip);

    // Broadcast (optional)
    window.dispatchEvent(
      new CustomEvent("zipdelivery:updated", {
        detail: { zip, triggerId: cfgLive.triggerId, source: "pdp" },
      })
    );

    closeModal(owner);
  });
}

// Store cfg on root as fallback
root.__cfgFallback = cfg;


    // initial render from cache
    const cached = getZipCache(cfg.storageKey, cfg.ttlDays);
    render(root, cfg, cached);

    // clicking ZIP (served + notserved) opens modal
    root.querySelectorAll(ZIP_SEL).forEach((el) => {
      el.addEventListener("click", (e) => {
        e.preventDefault();
        openModal(root, cfg);
      });
      el.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openModal(root, cfg);
        }
      });
    });

    // open buttons (nozip state)
    root.querySelectorAll(OPEN_SEL).forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        openModal(root, cfg);
      });
    });

    // ESC closes modal for this root
    document.addEventListener("keydown", (e) => {
  if (e.key !== "Escape") return;
  document.querySelectorAll(ROOT_SEL).forEach((root) => closeModal(root));
});

    // If overlay updates zip elsewhere, reflect it
    window.addEventListener("zipdelivery:updated", (ev) => {
      const z = normalizeZip(ev?.detail?.zip);
      if (!z) return;
      render(root, cfg, z);
    });
  };

  const boot = () => {
    document.querySelectorAll(ROOT_SEL).forEach(initRoot);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
